import { SelectedFieldInPreview } from '../../../../../types';

export const componentName = 'c-Watch';

// The portal preview opens the manage tab with no field focused. Treat that
// as if the landing title were selected, so the preview shows the actions
// view instead of an empty manage screen.
export const defaultManageSelectedField: SelectedFieldInPreview = {
  field: 'manage_my_plan_title',
  type: 'manage',
};

// Appends the `family_plan_` (linked / Family Plan) twin of every key, so the
// portal preview reacts to the twin fields exactly like their paired base.
const withFamilyPlanTwins = (fields: readonly string[]): string[] => {
  return [...fields, ...fields.map(field => `family_plan_${field}`)];
};

// WATCH PLAN SELECTION FIELDS
export const watchErrorFieldsMessages = withFamilyPlanTwins([
  'localization_generic_error',
  'multi_sim_plan_not_eligible',
  'localization_provisioning_error',
  'localization_authentication_error',
  'max_supported_secondary_devices_exceeded',
  'localization_renable_cellular_on_existing_plan_not_supported',
]);

// `close_button` is NOT an error field: the wizard collects it under Success
// States (shared at runtime by the error screen's close action), so its
// preview selection shows the success screen — see userFeedbackSuccess.
export const watchErrorFields = [
  ...withFamilyPlanTwins(['error_title']),
  ...watchErrorFieldsMessages,
];

// Twins included: selecting a family_plan_ twin in the portal preview must
// switch to the transfer T&C view exactly like its paired base field.
export const transferFields = withFamilyPlanTwins([
  'title',
  'content',
  'watch_transfer_watch_button',
]);

export const watchOnTransferFields = [
  'watch_transfer_plans_text',
  'watch_plan_display_title',
];

export const userFeedbackFields = withFamilyPlanTwins([
  'watch_activation_submitting_title',
  'watch_activation_success_title',
  'watch_activation_submitting_paragraph',
  'watch_activation_success_paragraph',
  'watch_transfer_submitting_title',
  'watch_transfer_success_title',
  'watch_transfer_submitting_paragraph',
  'watch_transfer_success_paragraph',
  'close_button',
]);

export const userFeedbackSuccess = withFamilyPlanTwins([
  'watch_transfer_success_title',
  'watch_transfer_success_paragraph',
  'watch_activation_success_title',
  'watch_activation_success_paragraph',
  'close_button',
]);

// Transfer aliases: since the watch tab also collects `close_button` under
// Success States now, both tabs share the same preview semantics — kept as
// separate exports so the tabs can diverge again without touching consumers.
export const transferFeedbackFields = [...userFeedbackFields];

export const transferSuccessFields = [...userFeedbackSuccess];

export const transferErrorFields = [...watchErrorFields];

export const userContractFields = [
  'contract_title',
  'contract_subtitle',
  'contract_content',
  'contract_continue',
  'contract_back',
] as const;

// Localisation keys for the cross-plan activation notification messages. These
// mirror the carrier-configurable text boxes on the portal watch config page and
// are looked up off the resolved `watch` localisation by `WatchNotification`
// value. ASYMMETRIC by design (matrix, GET /watch): each banner fires on one
// side only, so the wizard collects the paired notice as a base key and the
// linked notice as its family_plan_ twin — nothing else.
export const watchNotificationFields = [
  'notification_paired_activation_with_active_linked_plan',
  'family_plan_notification_linked_activation_with_active_paired_plan',
];

// Maps the `notification` attribute from GET /watch to its base localisation
// key. In linked mode the consumer prefers the `family_plan_` twin of the
// mapped key, falling back to this base key.
export const NOTIFICATION_KEY_MAP: Record<string, string> = {
  LINKED_ACTIVATION_WITH_ACTIVE_PAIRED_PLAN:
    'notification_linked_activation_with_active_paired_plan',
  PAIRED_ACTIVATION_WITH_ACTIVE_LINKED_PLAN:
    'notification_paired_activation_with_active_linked_plan',
};
// Family Plan (sa-watch / linked) localisation keys, across the plan-selection
// ('watch') and Terms & Conditions ('t&c') sections. Selecting any of these in the
// portal localisation editor previews the linked / Family Plan variant.
export const watchFamilyPlanFields = [
  'family_plan_title',
  'family_plan_content',
  'family_plan_continue',
  'family_plan_checkbox',
  // The wizard's mirror generates the twin off the base key `checkboxLabel`;
  // `family_plan_checkbox` above is kept for pre-mirror saved configs.
  'family_plan_checkboxLabel',
  'family_plan_link',
  'family_plan_back_button',
  // Twin fields whose selection must also flip the preview into linked mode:
  // cross-plan notices, error page copy and activation feedback.
  ...watchNotificationFields.filter(field => field.startsWith('family_plan_')),
  ...watchErrorFields.filter(field => field.startsWith('family_plan_')),
  'family_plan_watch_activation_submitting_title',
  'family_plan_watch_activation_submitting_paragraph',
  'family_plan_watch_activation_success_title',
  'family_plan_watch_activation_success_paragraph',
  'family_plan_close_button',
  // Contract-page twins (user_contract wizard group) — selecting one must
  // flip the preview into linked mode so ContractSummaryW resolves them.
  ...userContractFields.map(field => `family_plan_${field}`),
];

// Linked (sa-watch / standalone) variants of the watch-transfer copy keys. Each
// is the paired key prefixed with `family_plan_`. Selecting any of these in the
// portal localisation editor previews the linked transfer copy variant.
export const watchTransferFamilyPlanFields = [
  'family_plan_watch_plan_display_title',
  'family_plan_watch_transfer_plans_text',
  'family_plan_watch_transfer_submitting_title',
  'family_plan_watch_transfer_submitting_paragraph',
  'family_plan_watch_transfer_success_title',
  'family_plan_watch_transfer_success_paragraph',
  'family_plan_title',
  'family_plan_content',
  'family_plan_watch_transfer_watch_button',
  // Twin fields whose selection must also flip the transfer preview into
  // linked mode: transfer error page copy, success close button and the
  // plan-description flag.
  'family_plan_error_title',
  'family_plan_localization_generic_error',
  'family_plan_close_button',
  'family_plan_watch_transfer_disable_plan_description',
  // Transfer form consent copy (continue button + T&C acknowledgment line).
  'family_plan_continue',
  'family_plan_checkboxLabel',
  'family_plan_link',
] as const;
// WATCH PLAN SELECTION FIELDS END

// MANAGER FIELDS
export const manageErrorFields = withFamilyPlanTwins([
  'error_title',
  'close_button',
  'localization_generic_error',
]);

export const fallBackFields = withFamilyPlanTwins(['contact_us']);

export const loadingSuspendFields = withFamilyPlanTwins([
  'action_running_suspend',
  'action_running_suspend_description',
  'action_close', // This is a common field that will be used in all the actions, like loading or success
]);

export const loadingCancelFields = withFamilyPlanTwins([
  'action_running_cancel',
  'action_running_cancel_description',
]);

export const loadingReactivateFields = withFamilyPlanTwins([
  'action_running_reactivate',
  'action_running_reactivate_description',
]);

export const successSuspendFields = withFamilyPlanTwins([
  'action_succeeded_suspend',
  'action_succeeded_suspend_description',
]);

export const successCancelFields = withFamilyPlanTwins([
  'action_succeeded_cancel',
  'action_succeeded_cancel_description',
]);

export const successReactivateFields = withFamilyPlanTwins([
  'action_succeeded_reactivate',
  'action_succeeded_reactivate_description',
]);

export const defaultActionFields = withFamilyPlanTwins([
  'manage_my_plan_title',
  'watch_actions',
  'action_suspend',
  'action_cancel',
  'action_reactivate',
  'terms_and_conditions',
  'back_button',
]);

export const managePlanActions = withFamilyPlanTwins([
  'manage_my_plan_title',
  'watch_actions',
  'action_suspend',
  'action_cancel',
  'action_reactivate',
]);

export const managerActionsFields = [
  ...fallBackFields,
  ...loadingSuspendFields,
  ...loadingCancelFields,
  ...loadingReactivateFields,
  ...successSuspendFields,
  ...successCancelFields,
  ...successReactivateFields,
  ...defaultActionFields,
  ...managePlanActions,
];

export const cancelConfirmationFields = withFamilyPlanTwins([
  'cancel_confirmation_title',
  'cancel_confirmation_text',
  'yes_button',
  'no_button',
]);

// Family Plan (sa-watch / linked) variants of every manage-tab field.
// Selecting any of these in the portal localisation editor flips the manage
// preview into linked mode (parity with watchFamilyPlanFields /
// watchTransferFamilyPlanFields).
export const manageFamilyPlanFields = [
  ...managerActionsFields,
  ...cancelConfirmationFields,
  ...manageErrorFields,
].filter(field => field.startsWith('family_plan_'));
// END MANAGER FIELDS
